import { useEffect, useMemo, useRef, useState } from "react";
import type { Snapshot, Lane } from "../types";
import { LANE_LIST } from "../lanes";
import { fetchLanes, type LaneRow } from "./api";
import { TerminalPanel } from "./TerminalPanel";
import { SidePanel } from "../components/SidePanel";
import { apiBase } from "./api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtRel(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts).getTime();
  if (!d) return "—";
  const mins = Math.round((Date.now() - d) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function statusBadge(status: string): { label: string; cls: string } {
  switch (status) {
    case "embodied":
    case "working":
      return { label: status, cls: "bg-glow/20 text-glow border border-glow/40" };
    case "stuck":
    case "blocked":
    case "error":
      return { label: status, cls: "bg-bad/20 text-bad border border-bad/40" };
    case "done":
      return { label: "done", cls: "bg-good/20 text-good border border-good/30" };
    case "awaiting":
      return { label: "awaiting", cls: "bg-warn/20 text-warn border border-warn/40" };
    case "dead":
      return { label: "dead", cls: "bg-mist/10 text-mist/50 border border-mist/20" };
    default:
      return { label: status || "idle", cls: "bg-mist/10 text-mist border border-mist/20" };
  }
}

function isBlocked(status: string): boolean {
  return status === "blocked" || status === "stuck";
}

// ---------------------------------------------------------------------------
// Types for merged lane data
// ---------------------------------------------------------------------------

type MergedLane = {
  id: string;
  name: string;
  emoji: string;
  status: string;
  lastActive: string | null;
  queueDepth: number;
};

// ---------------------------------------------------------------------------
// HandoffPanel — fetches /api/handoffs/:lane and shows content
// ---------------------------------------------------------------------------

function HandoffPanel({ lane, onClose }: { lane: string; onClose: () => void }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${apiBase()}/api/handoffs/${encodeURIComponent(lane)}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: { lane?: string; content?: string }) => {
        if (alive) {
          setContent(data.content ?? "No handoff data found.");
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setContent("Could not load handoff data.");
          setLoading(false);
        }
      });
    return () => { alive = false; };
  }, [lane]);

  return (
    <SidePanel title={`Handoff — ${lane}`} onClose={onClose}>
      {loading ? (
        <div className="text-mist text-sm font-mono animate-pulse">loading…</div>
      ) : (
        <pre className="text-xs font-mono text-mist whitespace-pre-wrap break-words leading-relaxed">
          {content}
        </pre>
      )}
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// DeskCard — one card per lane
// ---------------------------------------------------------------------------

function DeskCard({
  lane,
  onOpenTerminal,
  onOpenHandoff,
}: {
  lane: MergedLane;
  onOpenTerminal: (id: string) => void;
  onOpenHandoff: (id: string) => void;
}) {
  const badge = statusBadge(lane.status);
  const blocked = isBlocked(lane.status);

  return (
    <div
      className="bg-panel border border-edge rounded px-3 py-2 cursor-pointer hover:border-mist/60 transition-colors"
      onClick={() => onOpenTerminal(lane.id)}
    >
      {/* Main row */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-base leading-none shrink-0">{lane.emoji}</span>
        <span className="font-mono text-sm text-white font-semibold shrink-0 w-16">{lane.name}</span>
        <span className={`font-mono text-xs px-1.5 py-0.5 rounded shrink-0 ${badge.cls}`}>
          {badge.label}
        </span>
        <span className="font-mono text-xs text-mist/70 truncate flex-1 min-w-0">
          {fmtRel(lane.lastActive)}
        </span>
        {lane.queueDepth > 0 && (
          <span className="font-mono text-xs bg-warn/20 text-warn border border-warn/30 rounded px-1.5 py-0.5 shrink-0">
            q:{lane.queueDepth}
          </span>
        )}
      </div>

      {/* Blocked row */}
      {blocked && (
        <div className="flex items-center gap-2 mt-1.5 pl-7">
          <span className="text-warn text-xs font-mono">⚠ Blocked</span>
          <button
            className="text-xs font-mono text-teal-400 hover:text-teal-300 underline underline-offset-2"
            onClick={(e) => { e.stopPropagation(); onOpenHandoff(lane.id); }}
          >
            open handoff →
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityFeed — war_room messages, newest first
// ---------------------------------------------------------------------------

function ActivityFeed({ messages }: { messages: Snapshot["war_room"] }) {
  if (!messages.length) {
    return <div className="text-mist/50 font-mono text-xs px-1">no recent activity</div>;
  }
  return (
    <div className="space-y-0.5">
      {messages.map((m, i) => (
        <div key={`${m.ts}-${i}`} className="flex items-baseline gap-2 font-mono text-xs py-0.5 border-b border-edge/40 last:border-0">
          <span className="text-mist/50 shrink-0 tabular-nums w-16">{fmtRel(m.ts)}</span>
          <span className="shrink-0">{m.emoji ?? ""}</span>
          <span className="text-mist shrink-0 w-14">{m.lane}</span>
          <span className="text-mist/80 truncate flex-1 min-w-0">
            {(m.message ?? "").slice(0, 80)}{(m.message?.length ?? 0) > 80 ? "…" : ""}
          </span>
          {m.handoff_to && (
            <span className="shrink-0 font-mono text-[10px] bg-teal-900/40 text-teal-400 border border-teal-700/40 rounded px-1.5 py-0.5">
              → {m.handoff_to}
            </span>
          )}
          {(m.count ?? 1) > 1 && (
            <span className="shrink-0 font-mono text-[10px] text-mist/50 bg-mist/10 border border-mist/20 rounded px-1.5 py-0.5">
              ×{m.count}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoomView — main export
// ---------------------------------------------------------------------------

const TOTAL_LANES = LANE_LIST.length; // 13

export function RoomView({ snap }: { snap: Snapshot | null }) {
  const [rows, setRows] = useState<LaneRow[]>([]);
  const [openLane, setOpenLane] = useState<string | null>(null);
  const [handoffLane, setHandoffLane] = useState<string | null>(null);
  const [, setNow] = useState(Date.now());
  const clockRef = useRef<number | undefined>(undefined);

  // Poll /lanes every 10s + tick clock every 15s to update relative times
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetchLanes(ac.signal);
        if (alive) setRows(r);
      } catch { /* keep last good rows */ }
    };
    tick();
    const poll = window.setInterval(tick, 10_000);
    clockRef.current = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => {
      alive = false;
      ac.abort();
      window.clearInterval(poll);
      window.clearInterval(clockRef.current);
    };
  }, []);

  // Merge static lane list with /lanes rows + snap lane status
  const lanes: MergedLane[] = useMemo(() => {
    const byName = new Map(rows.map((r) => [r.name.toLowerCase(), r]));
    const bySnap = new Map((snap?.fleet.lanes ?? []).map((l: Lane) => [l.id.toLowerCase(), l]));
    return LANE_LIST.map((l) => {
      const row = byName.get(l.id.toLowerCase());
      const sl = bySnap.get(l.id.toLowerCase());
      const status = (row?.status ?? sl?.status ?? "idle") as string;
      const lastActive = row?.last_active ?? sl?.last_action_ts ?? null;
      const queueDepth = sl?.queueDepth ?? 0;
      return { id: l.id, name: l.name, emoji: l.emoji, status, lastActive, queueDepth };
    });
  }, [rows, snap]);

  // Embodied lanes
  const embodiedLanes = useMemo(
    () => lanes.filter((l) => l.status === "embodied" || l.status === "working"),
    [lanes],
  );

  const messages = snap?.war_room ?? [];

  return (
    <div className="h-full w-full bg-ink overflow-auto scroll-thin">
      <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">

        {/* Embodied header */}
        <div className="flex items-center gap-3">
          {embodiedLanes.length === 0 ? (
            <span className="font-mono text-sm text-mist/50">
              0 / {TOTAL_LANES} embodied
            </span>
          ) : (
            <span className="font-mono text-sm text-mist">
              <span className="text-glow font-semibold">{embodiedLanes.length}</span>
              <span className="text-mist/60"> / {TOTAL_LANES} embodied</span>
              <span className="text-mist/60"> (</span>
              {embodiedLanes.map((l, i) => (
                <span key={l.id}>
                  {i > 0 && <span className="text-mist/40">, </span>}
                  <span className="text-glow">{l.name}</span>
                </span>
              ))}
              <span className="text-mist/60">)</span>
            </span>
          )}
        </div>

        {/* Desk card grid */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {lanes.map((lane) => (
            <DeskCard
              key={lane.id}
              lane={lane}
              onOpenTerminal={setOpenLane}
              onOpenHandoff={setHandoffLane}
            />
          ))}
        </div>

        {/* Activity feed */}
        <div className="bg-panel border border-edge rounded p-3">
          <div className="font-mono text-xs text-mist/50 uppercase tracking-widest mb-2">
            Activity · war room feed
          </div>
          <ActivityFeed messages={messages} />
        </div>

      </div>

      {/* Terminal panel */}
      {openLane && (
        <TerminalPanel lane={openLane} onClose={() => setOpenLane(null)} />
      )}

      {/* Handoff side panel */}
      {handoffLane && (
        <HandoffPanel lane={handoffLane} onClose={() => setHandoffLane(null)} />
      )}
    </div>
  );
}
